import { create } from 'zustand';
import { AudioEditor } from '@/pages/Home/Generator/module/AudioEditor';

export enum EditorOperatorEnum {
  NONE = 'none',
  INTRO = 'intro',
  TRIM = 'trim',
  REFILL = 'refill',
  EXTEND = 'extend',
  FADE_OUT = 'fadeout',
  OUTRO = 'outro',
}

export enum BezierEnum {
  EASE_IN_OUT = 'ease-in-out',
  EASE = 'ease',
  LINEAR = 'linear',
  EASE_IN = 'ease-in',
  EASE_OUT = 'ease-out',
}

type OperatorChangeListener = (operator: EditorOperatorEnum | undefined) => void;

export interface EditorStore {
  loading: boolean;
  setLoading: (loading: boolean) => void;
  editor: AudioEditor | undefined;
  setEditor: (editor: AudioEditor | undefined) => void;
  operator: EditorOperatorEnum;
  setOperator: (operator: EditorOperatorEnum | undefined) => void;
  toggleOperator: (from: EditorOperatorEnum | undefined, to: EditorOperatorEnum | undefined) => void;
  subscribeToOperator: (listener: OperatorChangeListener) => () => void;
  fideOutBezier: BezierEnum;
  setFideOutBezier: (bezier: BezierEnum) => void;
  duration: number;
  setDuration: (duration: number) => void;
  finalDuration: number;
  setFinalDuration: (finalDuration: number) => void;
  trimLoading: boolean;
  setTrimLoading: (loading: boolean) => void;
}

const useEditorStore = create<EditorStore>((set, get) => {
  const listeners = new Set<OperatorChangeListener>();

  const notifyListeners = (operator: EditorOperatorEnum | undefined) => {
    listeners.forEach((listener) => listener(operator));
  };

  return {
    loading: true,
    setLoading: (loading: boolean) => set({ loading }),
    trimLoading: false,
    setTrimLoading: (loading: boolean) => set({ trimLoading: loading }),
    editor: undefined,
    setEditor: (editor: AudioEditor | undefined) => set({ editor }),
    operator: EditorOperatorEnum.NONE,
    setOperator: (operator: EditorOperatorEnum | undefined) => {
      set({ operator });
      notifyListeners(operator);
    },
    toggleOperator: (from: EditorOperatorEnum | undefined, to: EditorOperatorEnum | undefined) => {
      const currentOperator = get().operator;
      const newOperator = currentOperator === from ? to : currentOperator || from;
      set({ operator: newOperator });
      notifyListeners(newOperator);
    },
    subscribeToOperator: (listener: OperatorChangeListener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    fideOutBezier: BezierEnum.EASE_IN_OUT,
    setFideOutBezier: (bezier: BezierEnum) => set({ fideOutBezier: bezier }),
    duration: 0,
    setDuration: (duration: number) => set({ duration }),
    finalDuration: 0,
    setFinalDuration: (finalDuration: number) => set({ finalDuration }),
  };
});

export default useEditorStore;
